import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import update from 'immutability-helper';
import { IdtSharedRenderService } from 'render/render-shared/idt-shared-render.service';
import { RenderStorageService } from 'render/render-storage/render-storage.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { RenderSlugService } from 'render/services/render-slug.service';
import { RenderStateService } from 'render/services/render-state/render-state.service';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AnimationsRenderService } from 'shared/animations/animations-render/animations-render.service';
import { UnsubscribeComponent } from 'shared/directives/unsubscribe.component';
import { TypeOfOverflow } from 'shared/idt-components/idt-components-cms.model';
import { IdtComponent, Orientation } from 'shared/models/idt-components/idt-component.models';
import { ResponsiveSetting } from 'shared/responsive-settings/responsive-settings.interface';
import { AmandaOptions, AssetsSharedService } from 'shared/services/assets-shared.service';
import { isIOSDevice } from 'shared/utils/device-type';
import { filterDict } from 'shared/utils/functools-map';


export interface SliceBaseHostStyles {
  backgroundColor: string;
  marginTop: string;
  marginRight: string;
  marginLeft: string;
  marginBottom: string;
  paddingTop: string;
  paddingRight: string;
  paddingLeft: string;
  paddingBottom: string;
}

interface SlotResponsiveConfig extends Partial<Exclude<Slot, 'responsiveConfig'>> {
  id: string;
}

export interface Slot {
  id?: string;
  hidden?: boolean;
  flexGrow: string;
  flexShrink: string;
  flexBasis: string;
  flexDirection: string;
  width: string;
  height: string;
  minHeight: string;
  maxHeight: string;
  minWidth: string;
  maxWidth: string;
  overflow?: TypeOfOverflow;
  responsiveConfig: [SlotResponsiveConfig];
}

@Directive() // eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class SliceBaseRenderComponent
  extends UnsubscribeComponent
  implements OnInit, OnDestroy {

  // TODO: should be IdtComponent<T>, declaring <T extends SliceBaseSettings<SliceBaseGeneralSettings>> at class level
  @Input() public slice!: IdtComponent;

  @HostBinding('style.background-color')
  public backgroundColor!: string;

  @HostBinding('style.margin-block-start')
  public marginTop!: string;

  @HostBinding('style.margin-inline-end')
  public marginRight!: string;

  @HostBinding('style.margin-inline-start')
  public marginLeft!: string;

  @HostBinding('style.margin-block-end')
  public marginBottom!: string;

  @HostBinding('style.padding-block-start')
  public paddingTop!: string;

  @HostBinding('style.padding-inline-end')
  public paddingRight!: string;

  @HostBinding('style.padding-inline-start')
  public paddingLeft!: string;

  @HostBinding('style.padding-block-end')
  public paddingBottom!: string;

  @HostBinding('style.border')
  public get border(): string {
    let result = 'none';
    if (this.slice?.settings?.styles) {
      const { borderStyle, borderWidth, borderColor } = this.slice.settings.styles;
      result = `${borderWidth} ${borderStyle} ${borderColor}`;
    }
    return result;
  }

  @HostBinding('style.border-radius')
  @HostBinding('class.overflow-hidden')
  public get componentBorderRadius(): string | undefined {
    if (this.slice?.settings?.styles?.borderRadiusType && this.slice?.settings?.styles?.borderRadiusType !== 'custom') {
      return undefined;
    }
    let result = 'unset';
    if (this.slice?.settings?.styles) {
      result = this.slice.settings.styles.borderRadius;
    }
    return (result !== '0px') ? result : undefined;
  }

  @HostBinding('style.background-image')
  public get backgroundImage(): SafeStyle | undefined {
    if (this.backgroundImageSetting) {
      const assetsUrl = this.renderConfigurationService.config.assetsUrl;
      const amandaOptions: AmandaOptions = {
        isWidthParamEnabled: this.renderConfigurationService.config.amandaImageVideoManagerEnabled,
        width: this.assetsSharedService.getWidth(this.el)
      };
      if (this.backgroundImageSetting?.impolicy) {
        amandaOptions.impolicy = this.backgroundImageSetting.impolicy;
      }
      // TODO: Have only one field into consideration after fixing back
      // Source: https://axinic.central.inditex.grp/bitbucket/projects/MECFRONT/repos/main/pull-requests/1590/overview
      const landscapeImage = this.backgroundImageSetting.landscapeImage?.image
                             || this.backgroundImageSetting.landscapeImage?.path;
      const portraitImage = this.backgroundImageSetting.portraitImage?.image
                            || this.backgroundImageSetting.portraitImage?.path;
      if (landscapeImage && this.orientation === 'landscape') {
        const absoluteUrl = this.assetsSharedService.getAssetUrl(landscapeImage, assetsUrl, amandaOptions);
        return this.domSanitizer.bypassSecurityTrustStyle(`url(${absoluteUrl})`);
      }
      if (portraitImage && this.orientation === 'portrait') {
        const absoluteUrl = this.assetsSharedService.getAssetUrl(portraitImage, assetsUrl, amandaOptions);
        return this.domSanitizer.bypassSecurityTrustStyle(`url(${absoluteUrl})`);
      }
    }
    return undefined;
  }

  @HostBinding('style.background-attachment')
  public get backgroundAttachment(): string | undefined {
    return this.backgroundImageSetting?.fixed && !isIOSDevice() ? 'fixed' : undefined;
  }

  @HostBinding('style.background-size')
  public get backgroundSize(): string | undefined {
    if (this.backgroundImageSetting) {
      return this.backgroundImageSetting.size ?? 'cover';
    }
    return undefined;
  }

  @HostBinding('style.background-repeat')
  public get backgroundRepeat(): string | undefined {
    if (this.backgroundImageSetting) {
      return this.backgroundImageSetting.repeat ?? 'no-repeat';
    }
    return undefined;
  }

  @HostBinding('style.background-position')
  public get backgroundPosition(): string | undefined {
    if (this.backgroundImageSetting) {
      return this.backgroundImageSetting.position ?? 'top';
    }
    return undefined;
  }

  @HostBinding('class')
  public get classes(): string {
    let classes: string = (this.slice?.settings?.styles?.shadow && this.slice.settings.styles.shadow !== 'none') ? `${this.slice.settings.styles?.shadow} ` : '';

    if (this.slice?.settings?.styles?.borderRadiusType) {
      if (this.slice.settings.styles.borderRadiusType === 'straight') {
        classes += 'border-radius-straight';
      } else if (this.slice.settings.styles.borderRadiusType === 'semi-round') {
        classes += 'border-radius-semi-round';
      } else if (this.slice.settings.styles.borderRadiusType === 'round') {
        classes += 'border-radius-round';
      }
    }

    return classes;
  }

  public orientation$: Observable<Orientation>;
  public orientation: Orientation = 'landscape';
  public components!: IdtComponent[];
  public slots: Slot[] = [];
  private animationsEventsDestroy?: () => void;

  constructor(
    protected readonly domSanitizer: DomSanitizer,
    protected readonly assetsSharedService: AssetsSharedService,
    protected readonly breakpointObserver: BreakpointObserver,
    protected readonly idtSharedRenderService: IdtSharedRenderService,
    protected readonly renderConfigurationService: RenderConfigurationService,
    protected readonly animationsRenderService: AnimationsRenderService,
    protected readonly cd: ChangeDetectorRef,
    protected readonly el: ElementRef<HTMLElement>,
    protected readonly renderStateService: RenderStateService,
    protected readonly renderStorageService: RenderStorageService,
    protected readonly renderSlugService: RenderSlugService
  ) {
    super();
    this.orientation$ = this.idtSharedRenderService.screenOrientation$ as Observable<Orientation>;
    this.renderStateService.getComponents$(this.renderSlugService.getSlug()).pipe(
      takeUntil(this.clearSubscriptions$)
    ).subscribe((components) => {
      this.components = components!;
    });
  }

  public async ngOnInit(): Promise<void> {
    if (this.slice && this.slice.settings) {
      if (this.slice.settings.animations) {
        this.animationsEventsDestroy = await this.animationsRenderService.setUp(this.el, this.slice.settings.animations);
      }

      this.setDefaultHostStyles();

      this.breakpointObserver.observe(
        this.renderConfigurationService.config.breakpoints.map((breakpoint) => breakpoint.bp)
      ).pipe(
        takeUntil(this.clearSubscriptions$)
      ).subscribe((breakpointState) => {
        this.onMediaChange(breakpointState);
        this.cd.markForCheck();
      });

      this.onSliceInit();

      this.orientation$.pipe(
        takeUntil(this.clearSubscriptions$)
      ).subscribe((orientation: Orientation) => {
        this.orientation = orientation;
      });
    }
  }

  public get backgroundImageSetting() {
    return this.slice?.settings?.styles?.backgroundImage;
  }

  public getSlotStyles(slot: Slot): Record<string, string> {
    const responsiveConfig: SlotResponsiveConfig | undefined = slot.responsiveConfig?.find(item => item.id === slot.id);
    return {
      'flex-grow': slot.flexGrow ?? '0',
      'flex-shrink': slot.flexShrink ?? '1',
      'flex-basis': slot.flexBasis ?? 'auto',
      width: slot.width ?? '100%',
      height: slot.height ?? 'auto',
      overflow: slot.overflow ?? 'visible',
      'min-height': responsiveConfig?.minHeight ?? slot.minHeight,
      'max-height': responsiveConfig?.maxHeight ?? slot.maxHeight,
      'min-width': responsiveConfig?.minWidth ?? slot.minWidth,
      'max-width': responsiveConfig?.maxWidth ?? slot.maxWidth,
    };
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();

    if (this.animationsEventsDestroy) {
      this.animationsEventsDestroy();
    }
  }

  protected abstract onSliceInit(): void;

  protected abstract setDefaultHostStyles(): void;

  protected abstract setResponsiveHostStyles(
    breakpointStyles: ResponsiveSetting,
    defaultBpSettings: Partial<ResponsiveSetting>
  ): void;

  protected onMediaChange(breakpointState: BreakpointState): void {
    const matches = filterDict(breakpointState.breakpoints, (key: string, value: boolean) => {
      return !!value;
    });
    const matchKeys = Object.keys(matches);

    if (matchKeys.length) {
      const mainMatch = matchKeys[0];
      const responsiveSlots = this.getResponsiveSlots(mainMatch).map((slot: Slot) => {
        if (!isDefined(slot.responsiveConfig)) {
          return slot;
        }
        const matchedResponsiveConfig = slot.responsiveConfig.find((rc: SlotResponsiveConfig) => rc.id === mainMatch);

        const newSlot = Object.keys(slot).reduce((acc, key) => {
          const responsiveKey = key as keyof SlotResponsiveConfig;
          const slotKey = key as keyof Slot;

          let value;
          if (isDefined(matchedResponsiveConfig) && isDefined(matchedResponsiveConfig[responsiveKey])) {
            value = matchedResponsiveConfig[responsiveKey];
          } else {
            value = slot[slotKey];
          }
          return {
            ...acc,
            [key]: value
          };
        }, slot);
        return newSlot;
      });

      responsiveSlots.forEach((slot, index) => {
        if (this.slots[index] === undefined) {
          this.slots[index] = slot;
        } else {
          Object.entries(slot).forEach(([property, value]) => {
            (this.slots[index][property as keyof Slot] as keyof Slot) = value;
          });
        }
      });
    } else {
      this.setDefaultHostStyles();
      this.slots = this.getDefaultSlotStructure(this.slice?.settings.slots);
    }
  }

  protected hasContent(slide: string): boolean {
    const slideComponent = this.components.find((c) => c.id === slide);

    if (slideComponent?.definitionId?.includes('piece-')) {
      return true;
    }

    const children = slideComponent && Object.values(slideComponent.children).map((child: string[]) => child[0]);
    const hasContent = children && children.some((child: string) => {
      const childComponent = this.components.find((c) => c.id === child);
      return childComponent?.definitionId?.includes('slice-')
        ? this.hasContent(childComponent.id)
        : childComponent?.definitionId?.includes('piece-');
    });
    return hasContent ?? false;
  }

  private getDefaultSlotStructure(slots: Slot[]): Slot[] {
    return slots.map((slot) => {
      return update(slot, {
        width: { $set: slot.width },
        hidden: { $set: slot.hidden ?? false }
      });
    });
  }

  private getResponsiveSlots(breakpoint: string): Slot[] {
    const defaultBpSettings = {
      marginTop: this.slice?.settings.styles.marginTop,
      marginBottom: this.slice?.settings.styles.marginBottom,
      marginLeft: this.slice?.settings.styles.marginLeft,
      marginRight: this.slice?.settings.styles.marginRight,
      paddingTop: this.slice?.settings.styles.paddingTop,
      paddingBottom: this.slice?.settings.styles.paddingBottom,
      paddingLeft: this.slice?.settings.styles.paddingLeft,
      paddingRight: this.slice?.settings.styles.paddingRight,
      structure: [
        this.slice?.settings.slots.map(() => 1)
      ]
    };

    const responsiveSettings: ResponsiveSetting[] = this.slice?.settings.styles.responsiveConfig || [];

    const breakpointStyles = responsiveSettings.find((responsiveSetting) => {
      return responsiveSetting.bp === breakpoint || responsiveSetting.id === breakpoint;
    }) || {} as ResponsiveSetting;

    this.setResponsiveHostStyles(breakpointStyles, defaultBpSettings);

    const slots = this.getSlotsStructure(this.slice?.settings.slots, breakpointStyles.structure || defaultBpSettings.structure);
    return this.hideSlotsWithEmptyReusableContent(slots);
  }

  private getSlotsStructure(sliceSlots: Slot[], structure: ResponsiveSetting['structure']): Slot[] {
    const finalSlots: Slot[] = [];
    const hiddenSlots = ([] as (string | number)[]).concat(...structure).map(it => !it);

    sliceSlots.forEach((slot, index) => {
      finalSlots.push(update(slot, { hidden: { $set: slot.hidden || hiddenSlots[index] } }));
    });

    return finalSlots;
  }

  private hideSlotsWithEmptyReusableContent(sliceSlots: Slot[]): Slot[] {
    const slots = [...sliceSlots];

    Object.entries(this.slice.children).forEach(([key, child]: [string, string[]]) => {
      const index = +key - 1;
      // If the slot was hidden, it remains hidden.
      if (child.length > 0 && index in slots && !slots[index].hidden) {
        update(slots[index], { hidden: { $set: this.isHiddenSlot(child[0]) } });
      }
    });

    return slots;
  }

  private isHiddenSlot(childId: string): boolean {
    const component = this.components.find((c) => c.id === childId);
    return !!component?.definitionId?.includes('slice-') && component.settings.slots?.length === 0;
  }

}
